import dayjs from "dayjs"
import {
    getProjectGrossProfitReviewBusinessOpportunitiesApi,
    getProjectGrossProfitReviewByUUIDApi,
    getProjectGrossProfitReviewKpiSavesApi,
    getProjectGrossProfitReviewLastSubmissionApi,
    getProjectGrossProfitReviewSubmissionApi,
    getProjectMilestonesApi,
    type GrossProfitReviewKpiMetricRaw,
    type GrossProfitReviewKpiSaveRaw,
    type GrossProfitReviewKpiSavesResp,
    type GrossProfitReviewSubmissionResp,
} from "@/services/apis/project"
import type {
    ReviewKind,
    ReviewListItem,
    ReviewMilestone,
    ReviewSavedSubmission,
    SignedMetricsPeriod,
    SignedReviewFormValue,
    UnsignedReviewDetail,
    ReviewDetail,
} from "./types"

/**
 * 复盘详情数据装配（前端接入后端现有接口，无需后端新增聚合接口）
 *
 * 数据来源：
 *  - 复盘记录：GET /workbenchApi/projects/project-gross-profit-reviews/:uuid
 *  - 毛利 / 人力投入指标：GET .../:uuid/kpi-saves
 *  - 商机列表（未签约会签约辅助填充）：GET .../:uuid/business-opportunities
 *    · metricList：创建/导入时绑定到复盘的稳定指标快照（优先用于已签约毛利表）
 *    · list：bi 库 PLM_PRJ_SHOW_KPI_SAVE 原始快照（用于兼容回退与未签约人力投入）
 *
 * 指标口径与 workbench/service/cockpit/member_load.go 保持一致：
 *  - 完成产值毛利 = 完成产值 / 1.0634 - 项目总成本 - 公司费用分摊 - 部门成本分摊
 *  - 实收利润     = 实收产值 - 工时人力成本 - 项目成本 - 人力成本分包 - 公司费用分摊 - 部门成本分摊 - 税金分摊
 *
 * PLM_PRJ_SHOW_KPI_SAVE 每行是截止 savedate 的累计快照，因此：
 *  - 累计     = 最新快照
 *  - 当月     = 最新快照 - 上一快照（没有上一快照时等于累计）
 *  - 当年累计 = 最新快照 - 今年之前的最后一个快照（没有时等于累计）
 */

/** 汇总型字段（做差得到期间值）；`projectProgress` 为百分比不参与做差 */
const DIFF_KEYS = [
    "manHourLaborCost",
    "projectCost",
    "laborCostSubcontract",
    "companyExpenseAllocation",
    "deptCostAllocation",
    "taxAllocation",
    "actualIncome",
    "invoiceAmount",
    "workDays",
    "workHours",
    "completionOutputValue",
    "receivableOutputValue",
    "projectTotalCost",
] as const

const round = (value: number, digits = 2): number => {
    if (!Number.isFinite(value)) return 0
    const factor = 10 ** digits
    return Math.round(value * factor) / factor
}

/** 后端返回金额为元，前端统一换算为万元展示 */
const toWan = (value: number): number => round(value / 10000)

/** 完成产值毛利（原始金额） */
const completionProfit = (kpi: GrossProfitReviewKpiSaveRaw): number =>
    kpi.completionOutputValue / 1.0634 -
    kpi.projectTotalCost -
    kpi.companyExpenseAllocation -
    kpi.deptCostAllocation

/** 完成产值毛利率（%） */
const completionProfitRate = (kpi: GrossProfitReviewKpiSaveRaw): number =>
    kpi.completionOutputValue ? (completionProfit(kpi) / kpi.completionOutputValue) * 100 : 0

/** 实收利润（原始金额） */
const actualProfit = (kpi: GrossProfitReviewKpiSaveRaw): number =>
    kpi.actualIncome -
    kpi.manHourLaborCost -
    kpi.projectCost -
    kpi.laborCostSubcontract -
    kpi.companyExpenseAllocation -
    kpi.deptCostAllocation -
    kpi.taxAllocation

/** 实收毛利率（%）：kpi-saves 未返回合同结算额，分母改用实收产值 */
const actualProfitRate = (kpi: GrossProfitReviewKpiSaveRaw): number =>
    kpi.actualIncome ? (actualProfit(kpi) / kpi.actualIncome) * 100 : 0

/** 按 saveDate 升序排序（后端按 savedate 倒序返回） */
const sortBySaveDate = (list: GrossProfitReviewKpiSaveRaw[]): GrossProfitReviewKpiSaveRaw[] =>
    [...list].sort((a, b) => (a.saveDate ?? "").localeCompare(b.saveDate ?? ""))

/** 两个累计快照做差，得到期间值 */
const diffKpi = (
    latest: GrossProfitReviewKpiSaveRaw,
    base: GrossProfitReviewKpiSaveRaw
): GrossProfitReviewKpiSaveRaw => {
    const result: GrossProfitReviewKpiSaveRaw = { ...latest }
    DIFF_KEYS.forEach((key) => {
        result[key] = (latest[key] ?? 0) - (base[key] ?? 0)
    })
    return result
}

/** 组装「当月 / 当年累计 / 累计」三时段指标；无快照时返回空数组 */
const buildPeriods = (list: GrossProfitReviewKpiSaveRaw[]): SignedMetricsPeriod[] => {
    const asc = sortBySaveDate(list)
    const latest = asc[asc.length - 1]
    if (!latest) return []

    const previous = asc[asc.length - 2]
    const yearStart = dayjs(latest.saveDate).startOf("year")
    const yearBase = asc.filter((v) => dayjs(v.saveDate).isBefore(yearStart)).pop()

    const toPeriod = (label: string, kpi: GrossProfitReviewKpiSaveRaw): SignedMetricsPeriod => ({
        label,
        completionOutput: toWan(kpi.completionOutputValue),
        completionProfit: toWan(completionProfit(kpi)),
        completionProfitRate: round(completionProfitRate(kpi)),
        actualIncome: toWan(kpi.actualIncome),
        actualProfitRate: round(actualProfitRate(kpi)),
    })

    return [
        toPeriod("当月", previous ? diffKpi(latest, previous) : latest),
        toPeriod("当年累计", yearBase ? diffKpi(latest, yearBase) : latest),
        toPeriod("累计", latest),
    ]
}

/** 复盘绑定指标快照（后端创建/导入时固化），用于优先展示稳定指标 */
const buildPeriodsFromMetricList = (list: GrossProfitReviewKpiMetricRaw[] = []): SignedMetricsPeriod[] =>
    list
        .filter((item) => !!item.indicator)
        .map((item) => ({
            label: item.indicator,
            completionOutput: toWan(item.completionOutputValue),
            completionProfit: toWan(item.completionOutputProfit),
            completionProfitRate: round(item.completionOutputProfitRate),
            actualIncome: toWan(item.actualIncome),
            actualProfitRate: round(item.actualProfitRate),
        }))

/** 未签约项目：由快照派生的九项人力投入信息 */
type UnsignedLaborFields = Pick<
    UnsignedReviewDetail,
    "firstReportMonth" | "investedMonths" | "monthLaborCost" | "yearLaborCost" | "totalLaborCost"
>

const buildLaborFields = (
    list: GrossProfitReviewKpiSaveRaw[],
    fallbackMonth: string
): UnsignedLaborFields => {
    const asc = sortBySaveDate(list)
    const latest = asc[asc.length - 1]
    if (!latest) {
        return {
            firstReportMonth: fallbackMonth,
            investedMonths: 0,
            monthLaborCost: 0,
            yearLaborCost: 0,
            totalLaborCost: 0,
        }
    }

    const first = asc[0]
    const previous = asc[asc.length - 2]
    const yearStart = dayjs(latest.saveDate).startOf("year")
    const yearBase = asc.filter((v) => dayjs(v.saveDate).isBefore(yearStart)).pop()

    const monthLaborCost = previous
        ? latest.manHourLaborCost - previous.manHourLaborCost
        : latest.manHourLaborCost
    const yearLaborCost = yearBase
        ? latest.manHourLaborCost - yearBase.manHourLaborCost
        : latest.manHourLaborCost

    return {
        firstReportMonth: first.saveDate ? dayjs(first.saveDate).format("YYYY-MM") : fallbackMonth,
        investedMonths: Math.max(1, dayjs(latest.saveDate).diff(dayjs(first.saveDate), "month") + 1),
        monthLaborCost: toWan(monthLaborCost),
        yearLaborCost: toWan(yearLaborCost),
        totalLaborCost: toWan(latest.manHourLaborCost),
    }
}

/**
 * 优先使用复盘绑定快照（metricList）固化的「工时人力成本」：
 * 后台创建/导入时按「指标」固化，当月 / 当年累计 / 累计 行即 本月 / 当年 / 累计 投入人力成本，
 * 直接取用可避免旧口径（相邻 savedate 快照做差）在同一次保存按专业拆成多行、
 * 或同一 savedate 存在多个指标行时取错基准值导致本月投入偏小（如 0.02）。
 * 快照未包含该字段（历史数据未重新固化）时返回原值，回退旧口径计算。
 */
const applyLaborCostFromMetricList = (
    fields: UnsignedLaborFields,
    metricList: GrossProfitReviewKpiMetricRaw[] = []
): UnsignedLaborFields => {
    const pick = (...indicators: string[]) =>
        metricList.find((item) => indicators.includes((item.indicator ?? "").trim()))
    const month = pick("当月")
    const year = pick("当年累计", "当年")
    const total = pick("累计")
    const hasSnapshot =
        month?.manHourLaborCost != null ||
        year?.manHourLaborCost != null ||
        total?.manHourLaborCost != null
    if (!hasSnapshot) return fields
    return {
        ...fields,
        monthLaborCost: toWan(month?.manHourLaborCost ?? 0),
        yearLaborCost: toWan(year?.manHourLaborCost ?? 0),
        totalLaborCost: toWan(total?.manHourLaborCost ?? 0),
    }
}

/** 项目投入曲线（项目仪表盘成本页） */
const buildCurveUrl = (opProjectID?: number): string => {
    if (!opProjectID) return ""
    return new URL(`/projects/${opProjectID}/dashboard?tab=cost`, window.location.origin).href
}

/** A2 里程碑页（固定站点，按 opProjectID 拼接 /projects/{opProjectID}/milestone） */
const buildMilestoneUrl = (opProjectID?: number): string => {
    if (!opProjectID) return ""
    return `https://ith-workspace.thape.com.cn/projects/${opProjectID}/milestone`
}

/**
 * A2 里程碑只读列表装配（与里程碑页「节点时间与状态设置」同数据源）：
 *  - 按 pm 默认顺序 udf9 升序
 *  - 无「项目开始」节点时补一个占位首节点（与里程碑页展示一致）
 */
const buildMilestones = (list: API.PROJECT.Milestone[]): ReviewMilestone[] => {
    const items: ReviewMilestone[] = [...list]
        .sort((a, b) => (a.udf9 ?? 0) - (b.udf9 ?? 0))
        .map((m) => ({
            milestoneCode: m.milestoneCode,
            milestoneName: m.milestoneName,
            planDate: m.milestoneDate || "",
            actualDate: m.actualDate || "",
            status: m.status,
            isAdvanceInvoice: m.isAdvanceInvoice,
            confirmations: (m.confirmationAttachs ?? []).map((a, i) => ({
                uid: `${m.milestoneCode}-${i}`,
                name: a.fileName,
                url: a.edocUrl,
            })),
        }))
    if (items.length > 0 && !items.some((m) => m.milestoneCode === "start_time")) {
        items.unshift({
            milestoneCode: "start_time",
            milestoneName: "项目开始",
            planDate: "",
            actualDate: "",
            status: 0,
            confirmations: [],
        })
    }
    return items
}

/**
 * 当前保存内容（暂存 / 提交，回填用）装配：
 *  - 读取失败 → savedSubmissionError=true（按无保存内容处理并提示，不阻塞填报）
 *  - 无保存记录 / payload 为空 → 不含 savedSubmission
 *  - 有保存内容 → 附带 kind / status / submittedAt / payload（kind 是否匹配由表单初始化时判断）
 */
const buildSavedSubmission = (
    expectedKind: ReviewKind,
    submission: GrossProfitReviewSubmissionResp | null,
    loadError: boolean
): { savedSubmission?: ReviewSavedSubmission; savedSubmissionError?: boolean } => {
    if (loadError) return { savedSubmissionError: true }
    if (!submission?.payload) return {}
    return {
        savedSubmission: {
            status: submission.status === "submitted" ? "submitted" : "draft",
            kind: submission.kind ?? expectedKind,
            submittedAt: submission.submittedAt ?? undefined,
            payload: submission.payload,
        },
    }
}

/**
 * 获取复盘详情（列表项 id 即后端 uuid）
 * kpi-saves / last-submission / submission 失败不阻塞详情展示：
 * 指标 / 人力投入 / 上次复盘降级为空，当前保存内容按无处理（submission 失败另标记 savedSubmissionError），其余信息仍可查看
 */
export const getReviewDetail = async (uuid: string): Promise<ReviewDetail> => {
    const reviewPromise = getProjectGrossProfitReviewByUUIDApi(uuid)
    // 里程碑列表（A2 只读展示）：依赖详情中的 opProjectID，故由详情请求链式触发；失败不阻塞详情，降级为空数组
    const milestonesPromise = reviewPromise.then((d) =>
        d.isSigned && d.opProjectID ? getProjectMilestonesApi(d.opProjectID).catch(() => []) : []
    )
    const [detail, kpi, lastSubmission, submission, opportunities, milestones] = await Promise.all([
        reviewPromise,
        getProjectGrossProfitReviewKpiSavesApi(uuid).catch(() => ({
            list: [] as GrossProfitReviewKpiSaveRaw[],
            metricList: [] as GrossProfitReviewKpiMetricRaw[],
            expectedProfitRate: undefined as number | null | undefined,
        })),
        // 上次复盘内容：同项目编码上一条已提交记录（连续触发复盘时展示），失败按无处理
        getProjectGrossProfitReviewLastSubmissionApi(uuid).catch(() => null),
        // 当前复盘自己的最新保存内容（暂存 / 提交，回填用）；区分「无保存记录」与「读取失败」
        getProjectGrossProfitReviewSubmissionApi(uuid)
            .then((res) => ({ res, error: false }))
            .catch(() => ({ res: null, error: true })),
        // 商机列表失败不阻塞详情，降级为空数组
        getProjectGrossProfitReviewBusinessOpportunitiesApi(uuid).catch(() => ({
            projectOpportunities: [],
            projectOpportunitiesByProjectCode: [],
        })),
        milestonesPromise,
    ])

    const kind: ReviewKind = detail.isSigned ? "signed" : "unsigned"
    const saved = buildSavedSubmission(kind, submission.res, submission.error)

    const kpiList = kpi?.list ?? []
    const reviewMonth = detail.reviewMonth || (detail.CreatedAt ? dayjs(detail.CreatedAt).format("YYYY-MM") : "")
    // 单条详情接口不返回提交状态，由当前保存内容的 status 派生（submitted → 已提交，锁定进入只读预览）
    const submissionStatus = (saved.savedSubmission?.status ??
        "none") as ReviewListItem["submissionStatus"]
    const base = {
        id: detail.uuid,
        projectName: detail.projectName,
        projectNo: detail.code,
        department: detail.departmentName,
        manager: detail.managerName,
        reviewMonth,
        submissionStatus,
        submittedAt: saved.savedSubmission?.submittedAt,
        ...saved,
    }

    if (detail.isSigned) {
        const asc = sortBySaveDate(kpiList)
        const periodsFromMetricList = buildPeriodsFromMetricList(kpi?.metricList ?? [])
        // 上次复盘内容：取上一条已提交记录的改进举措（用于对比改进举措是否落地）
        const lastPayload = lastSubmission?.payload as SignedReviewFormValue | null | undefined
        const lastReview = lastPayload?.improvement?.trim() || undefined
        // 预计毛利率（%）：优先取复盘记录从 bi 库 PLM_PRJ_EST_MARGIN 固化的 expectedProfitRate，回退 kpi-saves 返回值
        const rawExpectedProfitRate = detail.expectedProfitRate ?? kpi?.expectedProfitRate
        const expectedProfitRate =
            rawExpectedProfitRate !== undefined &&
            rawExpectedProfitRate !== null &&
            Number.isFinite(Number(rawExpectedProfitRate))
                ? round(Number(rawExpectedProfitRate))
                : undefined
        return {
            ...base,
            kind: "signed",
            expectedProfitRate,
            metrics: {
                target: asc[asc.length - 1]?.indicator || "-",
                periods: periodsFromMetricList.length > 0 ? periodsFromMetricList : buildPeriods(kpiList),
            },
            curveUrl: buildCurveUrl(detail.opProjectID),
            milestoneUrl: buildMilestoneUrl(detail.opProjectID),
            milestones: buildMilestones(milestones),
            lastReview,
        }
    }

    return {
        ...base,
        kind: "unsigned",
        ...applyLaborCostFromMetricList(buildLaborFields(kpiList, reviewMonth), kpi?.metricList ?? []),
        projectOpportunities: opportunities.projectOpportunities,
        projectOpportunitiesByProjectCode: opportunities.projectOpportunitiesByProjectCode,
    }
}
